import {
  createContext,
  createSignal,
  onMount,
  useContext,
  type ParentComponent,
} from "solid-js";
import { api, clearTokens, getAccessToken, setTokens } from "../api/client";
import type { Profile, UserBrief } from "../api/types";
import { initLocale, setAppLocale } from "../i18n";
import { invalidateBodyWeightCache } from "../lib/bodyWeightCache";
import {
  cacheUser,
  cachedFetch,
  readCache,
  setCacheUser,
  writeCache,
} from "../lib/cache";
import { prefetchCurrentCalendar } from "../lib/data";
import {
  getTelegramDebugInfo,
  getTelegramWebApp,
  initTelegramUI,
  isTelegramMiniApp,
} from "../lib/telegram";

interface AuthState {
  user: () => UserBrief | Profile | null;
  profile: () => Profile | null;
  loading: () => boolean;
  authenticated: () => boolean;
  loginWithWebApp: () => Promise<void>;
  loginWithWidget: (payload: Record<string, string | number>) => Promise<void>;
  refreshProfile: () => Promise<void>;
  logout: () => Promise<void>;
  error: () => string | null;
  debug: () => ReturnType<typeof getTelegramDebugInfo>;
}

const AuthContext = createContext<AuthState>();

export const AuthProvider: ParentComponent = (props) => {
  const [user, setUser] = createSignal<UserBrief | Profile | null>(null);
  const [profile, setProfile] = createSignal<Profile | null>(null);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<string | null>(null);
  const [debug, setDebug] = createSignal(getTelegramDebugInfo());

  function applyProfile(p: Profile) {
    // Skip identical data so the screen does not re-render for nothing.
    if (JSON.stringify(p) === JSON.stringify(profile())) return;
    setProfile(p);
    setUser(p);
    setAppLocale(p.language.startsWith("en") ? "en" : "ru");
  }

  async function refreshProfile() {
    const p = await cachedFetch("profile", () => api.getProfile());
    if (cacheUser() !== p.id) {
      // First profile of this session (no id known before): bind the cache to it.
      setCacheUser(p.id);
      writeCache("profile", p);
    }
    applyProfile(p);
  }

  /**
   * Profile and the first screen's data start together. With a cached profile
   * the app opens at once and the profile revalidates in the background.
   */
  async function loadHome(userId: string) {
    setCacheUser(userId);
    const cached = readCache<Profile>("profile");
    if (window.location.pathname === "/") void prefetchCurrentCalendar();
    const fresh = refreshProfile();
    if (!cached) return fresh;
    applyProfile(cached);
    fresh.catch((e) => {
      console.warn("[auth] profile revalidation failed:", e);
      if (!getAccessToken()) {
        // Session is gone (refresh failed): do not keep showing cached data.
        setUser(null);
        setProfile(null);
        setError(e instanceof Error ? e.message : "Session expired");
      }
    });
  }

  async function loginWithWebApp() {
    const wa = getTelegramWebApp();
    if (!wa?.initData) throw new Error("No Telegram initData");
    const res = await api.loginWebApp(wa.initData);
    setTokens(res.accessToken, res.refreshToken);
    setUser(res.user);
    initLocale(res.user.language);
    await loadHome(res.user.id);
    setError(null);
  }

  async function loginWithWidget(payload: Record<string, string | number>) {
    // Normalize: Telegram may pass numbers; keep only signed fields
    const clean: Record<string, string | number> = {};
    for (const key of [
      "id",
      "first_name",
      "last_name",
      "username",
      "photo_url",
      "auth_date",
      "hash",
    ] as const) {
      const v = payload[key];
      if (v !== undefined && v !== null && v !== "") {
        clean[key] = v;
      }
    }

    const res = await api.loginWidget(clean);
    if (!res?.accessToken || !res?.user) {
      throw new Error("Empty auth response from server");
    }

    setTokens(res.accessToken, res.refreshToken);
    setUser(res.user);
    initLocale(res.user.language);
    setError(null);

    try {
      await loadHome(res.user.id);
    } catch (e) {
      // Tokens + user are already set — do not fail the whole login
      console.warn("[auth] profile refresh after widget login failed:", e);
    }
  }

  async function logout() {
    try {
      await api.logout();
    } catch {
      clearTokens();
    }
    setUser(null);
    setProfile(null);
    invalidateBodyWeightCache();
  }

  onMount(async () => {
    initTelegramUI();
    initLocale();
    setDebug(getTelegramDebugInfo());

    try {
      if (isTelegramMiniApp()) {
        try {
          await loginWithWebApp();
        } catch (e) {
          clearTokens();
          const msg = e instanceof Error ? e.message : "Mini App auth failed";
          setError(msg);
          console.error(
            "[auth] Mini App login failed:",
            e,
            getTelegramDebugInfo(),
          );
        }
      } else if (getAccessToken()) {
        try {
          const uid = cacheUser();
          const cached = uid ? readCache<Profile>("profile") : undefined;
          if (uid && cached) {
            // Same session as the cache (tokens and cache are cleared together).
            await loadHome(uid);
          } else {
            await refreshProfile();
          }
        } catch (e) {
          clearTokens();
          if (isTelegramMiniApp()) {
            try {
              await loginWithWebApp();
              return;
            } catch (e2) {
              setError(e2 instanceof Error ? e2.message : "Auth error");
            }
          } else {
            setError(e instanceof Error ? e.message : "Session expired");
          }
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Auth error");
      clearTokens();
    } finally {
      setDebug(getTelegramDebugInfo());
      setLoading(false);
    }
  });

  const value: AuthState = {
    user,
    profile,
    loading,
    authenticated: () => Boolean(user()),
    loginWithWebApp,
    loginWithWidget,
    refreshProfile,
    logout,
    error,
    debug,
  };

  return (
    <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
  );
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
